(() => {
  // p = 动画时间轴进度 0→1。三幕各自的实际滚动距离由主题配置控制。
  //
  // —— 桌面时间轴 ——
  // 0.00–0.05  下滑提示淡出
  // 0.03–0.10  标题出现（上移淡入）
  // 0.06–0.13  副标题出现
  // 0.10–0.17  手机出现（Act1 小尺寸、叠在猫砂盆右侧）
  // 0.14–0.20  左侧 Olive 卡片出现
  // 0.17–0.23  右上 Pee / 通知卡出现
  // 0.20–0.26  左侧 Callie 卡片出现
  // 0.34–0.44  Act1 固定层（猫砂盆）淡出；同时手机开始往 Act2 位置飞
  // 0.34–0.50  手机从 Act1 位/缩放 → Act2 居中放大
  // 0.46–0.54  左侧 shared 标题 + Act2 文案/列表出现
  // 0.50–0.84  Act2 右侧 reel 逐条滚
  // 0.58–0.87  手机屏幕 1→2→3→4 切换
  // 0.84–0.90  背景米白 → 黑；标题变白
  // 0.84–0.87  Act2 淡出，屏幕切到 AI
  // 0.87–0.92  Act3 出现
  // 0.92–1.00  Act3 reel 逐条滚
  //
  // applyReveal(start, end)：start→end 淡入并上移，到 FADE_S→FADE_E 再淡出

  const STAGE_W = 1920; // 设计稿舞台宽，用来等比缩放
  const STAGE_H = 930; // 设计稿舞台高
  const M_STAGE_W = 375; // 手机设计稿宽
  const M_STAGE_H = 660; // 手机设计稿高（画布在视口里等比缩放）
  const PHONE_S1_X = 404; // Act1 手机相对舞台中心的 X 偏移（越大越靠右）
  const PHONE_S1_Y = 240; // Act1 手机相对舞台中心的 Y 偏移（越大越靠下）
  const PHONE_S1_SCALE = 0.6625; // Act1 手机缩放
  const PHONE_S2_SCALE = 1; // Act2 手机缩放（居中后）
  const REEL_STEP = 96; // 每条 reel 行高
  const REEL_H = 320; // reel 窗口高度
  const REEL_FOCAL = REEL_H / 2; // 当前条对准窗口垂直中心
  const REEL_SHIFT = 38; // 当前条往左错开的距离
  const RISE = 20; // 桌面元素淡入时从下方升起的像素
  const FADE_S = 0.34; // Act1 图层开始淡出
  const FADE_E = 0.44; // Act1 图层完全消失
  const MQ_MOBILE = '(max-width: 989px)';
  // spans 以视口高度为单位；Act2 的列表和切换过渡共用一个长度配置。
  const DESKTOP_SCROLL_SCENE = {
    stops: [0, 0.5, 0.84, 0.92, 1],
    spans: [3.5, 0.56, 0.56, 0.56],
  };
  const MOBILE_SCROLL_SCENE = {
    stops: [0, 0.48, 0.8, 0.87, 1],
    spans: [2.88, 0.78, 0.42, 0.78],
  };

  const clamp = (v, min, max) => Math.min(max, Math.max(min, v));
  const lerp = (a, b, t) => a + (b - a) * t;
  const scrollRatio = (value) => {
    const percent = Number(value);
    return clamp(Number.isFinite(percent) && percent > 0 ? percent : 100, 10, 200) / 100;
  };

  function interp(p, stops, values) {
    if (p <= stops[0]) return values[0];
    const last = stops.length - 1;
    if (p >= stops[last]) return values[last];
    for (let i = 0; i < last; i++) {
      if (p >= stops[i] && p <= stops[i + 1]) {
        const t = (p - stops[i]) / (stops[i + 1] - stops[i] || 1);
        const a = values[i];
        const b = values[i + 1];
        if (typeof a === 'number' && typeof b === 'number') return lerp(a, b, t);
        return t < 0.5 ? a : b;
      }
    }
    return values[last];
  }

  function hexToRgb(hex) {
    const h = hex.replace('#', '');
    const n = parseInt(h.length === 3 ? h.split('').map((c) => c + c).join('') : h, 16);
    return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
  }

  function lerpColor(a, b, t) {
    const A = hexToRgb(a);
    const B = hexToRgb(b);
    return `rgb(${Math.round(lerp(A.r, B.r, t))}, ${Math.round(lerp(A.g, B.g, t))}, ${Math.round(lerp(A.b, B.b, t))})`;
  }

  function interpColor(p, stops, colors) {
    if (p <= stops[0]) return colors[0];
    const last = stops.length - 1;
    if (p >= stops[last]) return colors[last];
    for (let i = 0; i < last; i++) {
      if (p >= stops[i] && p <= stops[i + 1]) {
        const t = (p - stops[i]) / (stops[i + 1] - stops[i] || 1);
        return lerpColor(colors[i], colors[i + 1], t);
      }
    }
    return colors[last];
  }

  function reveal(p, start, end, finalOpacity = 1) {
    return {
      opacity: interp(p, [start, end, FADE_S, FADE_E], [0, finalOpacity, finalOpacity, 0]),
      y: interp(p, [start, end], [RISE, 0]),
    };
  }

  function reelValue(p, range, count) {
    if (count <= 1) return 0;
    const stops = [];
    const values = [];
    for (let i = 0; i < count; i++) {
      stops.push(range[0] + ((range[1] - range[0]) * i) / (count - 1));
      values.push(i);
    }
    return interp(p, stops, values);
  }

  function setOpacity(el, opacity) {
    if (!el) return;
    el.style.opacity = String(opacity);
    el.style.visibility = opacity < 0.01 ? 'hidden' : 'visible';
  }

  function applyReveal(el, p, start, end) {
    if (!el) return;
    const r = reveal(p, start, end);
    el.style.opacity = String(r.opacity);
    el.style.transform = `translate3d(0, ${r.y}px, 0)`;
    el.style.visibility = r.opacity < 0.01 ? 'hidden' : 'visible';
  }

  function mReveal(el, p, start, end, fadeStart = 0.3, fadeEnd = 0.4, x = '0px') {
    if (!el) return;
    const opacity = interp(p, [start, end, fadeStart, fadeEnd], [0, 1, 1, 0]);
    const y = interp(p, [start, end], [16, 0]);
    el.style.opacity = String(opacity);
    el.style.transform = `translate3d(${x}, ${y}px, 0)`;
    el.style.visibility = opacity < 0.01 ? 'hidden' : 'visible';
  }

  class DlPageScrollyLumaY extends HTMLElement {
    connectedCallback() {
      this.learnMoreButtons = this.querySelectorAll('[data-scrolly-learn-more]');
      this.learnMorePopup = this.querySelector('[data-scrolly-popup] dl-pop-base-v2');
      this.learnMorePopupMain = this.learnMorePopup?.querySelector('.dl-pop-base-h');
      this.onLearnMoreClick = this.handleLearnMoreClick.bind(this);
      this.learnMoreButtons.forEach((button) => {
        button.addEventListener('click', this.onLearnMoreClick);
      });
      this.popupProductForms = this.querySelectorAll('[data-scrolly-popup] dl-product-form-h');
      this.onPopupProductFormClick = this.handlePopupProductFormClick.bind(this);
      this.popupProductForms.forEach((form) => {
        form.addEventListener('click', this.onPopupProductFormClick);
      });
      if (this.learnMorePopupMain) {
        this.popupStateObserver = new MutationObserver(() => this.syncPopupSceneState());
        this.popupStateObserver.observe(this.learnMorePopupMain, {
          attributes: true,
          attributeFilter: ['class'],
        });
        this.syncPopupSceneState();
      }

      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.classList.add('dl-scrolly-luma-y--reduced');
        return;
      }

      this.scrollRatios = [
        scrollRatio(this.dataset.act1ScrollLength),
        scrollRatio(this.dataset.act2ScrollLength),
        scrollRatio(this.dataset.act3ScrollLength),
      ];
      this.desktopTrack = this.querySelector('.dl-scrolly-luma-y__track');
      this.mobileTrack = this.querySelector('[data-m-track]');
      this.stage = this.querySelector('[data-scrolly-stage]');
      this.bg = this.querySelector('[data-scrolly-bg]');
      this.bottomFade = this.querySelector('[data-scrolly-bottom-fade]');
      this.phone = this.querySelector('[data-scrolly-phone]');
      this.phoneReveal = this.querySelector('[data-scrolly-phone-reveal]');
      this.screens = this.querySelectorAll('[data-scrolly-screen]');
      this.act1Fixed = this.querySelectorAll('[data-act1-fixed]');
      this.heading = this.querySelector('[data-act1-heading]');
      this.sub = this.querySelector('[data-act1-sub]');
      this.olive = this.querySelector('[data-act1-olive]');
      this.pee = this.querySelector('[data-act1-pee]');
      this.callie = this.querySelector('[data-act1-callie]');
      this.sharedTitle = this.querySelector('[data-shared-title]');
      this.sharedTitleText = this.querySelector('[data-shared-title-text]');
      this.act2 = this.querySelector('[data-act2]');
      this.act3 = this.querySelector('[data-act3]');
      this.act3LearnMore = this.querySelector('[data-act3-learn-more]');
      this.hint = this.querySelector('[data-scrolly-hint]');
      this.reel2Track = this.querySelector('[data-reel="2"] [data-reel-track]');
      this.reel3Track = this.querySelector('[data-reel="3"] [data-reel-track]');
      this.reel2Items = this.querySelectorAll('[data-reel="2"] [data-reel-item]');
      this.reel3Items = this.querySelectorAll('[data-reel="3"] [data-reel-item]');

      this.mBg = this.querySelector('[data-m-bg]');
      this.mMask = this.querySelector('[data-m-mask]');
      this.mBottomFade = this.querySelector('[data-m-bottom-fade]');
      this.mAct1BottomFade = this.querySelector('[data-m-act1-bottom-fade]');
      this.mAct1 = this.querySelector('[data-m-act1]');
      this.mPlans = this.querySelector('[data-m-plans]');
      this.mHeading = this.querySelector('[data-m-act1-heading]');
      this.mSub = this.querySelector('[data-m-act1-sub]');
      this.mLitter = this.querySelector('[data-m-litter]');
      this.mOlive = this.querySelector('[data-m-olive]');
      this.mCallie = this.querySelector('[data-m-callie]');
      this.mPee = this.querySelector('[data-m-pee]');
      this.mPhoneAct1 = this.querySelector('[data-m-phone-act1]');
      this.mPhone = this.querySelector('[data-m-phone]');
      this.mPhoneFade = this.querySelector('[data-m-phone-fade]');
      this.mScreens = this.querySelectorAll('[data-m-screen]');
      this.mPlansTitle = this.querySelector('[data-m-plans-title]');
      this.mPlan2 = this.querySelector('[data-m-plan2]');
      this.mPlan3 = this.querySelector('[data-m-plan3]');
      this.mReels = this.querySelector('[data-m-reels]');
      this.mReel2 = this.querySelector('[data-m-reel="2"]');
      this.mReel3 = this.querySelector('[data-m-reel="3"]');
      this.mReel2Items = this.querySelectorAll('[data-m-reel="2"] [data-m-reel-item]');
      this.mReel3Items = this.querySelectorAll('[data-m-reel="3"] [data-m-reel-item]');
      this.mHint = this.querySelector('[data-m-hint]');
      this.mSticky = this.querySelector('[data-m-sticky]');
      this.mCanvas = this.querySelector('[data-m-canvas]');

      this.raf = 0;
      this.onScroll = () => {
        if (this.raf) return;
        this.raf = requestAnimationFrame(() => {
          this.raf = 0;
          this.update();
        });
      };
      this.onResize = () => {
        this.act1PhoneFrom = null;
        this.updateScale();
        this.update();
      };

      this.act1PhoneFrom = null;
      this.updateScale();
      this.update();
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize);
    }

    disconnectedCallback() {
      this.learnMoreButtons?.forEach((button) => {
        button.removeEventListener('click', this.onLearnMoreClick);
      });
      this.popupProductForms?.forEach((form) => {
        form.removeEventListener('click', this.onPopupProductFormClick);
      });
      this.cartUpdateUnsubscribe?.();
      this.cartUpdateUnsubscribe = null;
      this.popupStateObserver?.disconnect();
      this.popupStateObserver = null;
      this.classList.remove('dl-popup-open');
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
      if (this.raf) cancelAnimationFrame(this.raf);
    }

    handleLearnMoreClick(event) {
      event.preventDefault();
      event.stopPropagation();
      this.learnMorePopup?.show?.();
    }

    syncPopupSceneState() {
      this.classList.toggle(
        'dl-popup-open',
        Boolean(this.learnMorePopupMain?.classList.contains('dl-show')),
      );
    }

    handlePopupProductFormClick() {
      if (typeof window.subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

      this.cartUpdateUnsubscribe?.();
      this.cartUpdateUnsubscribe = window.subscribe(PUB_SUB_EVENTS.cartUpdate, (payload) => {
        if (payload?.source !== 'product-form') return;
        this.learnMorePopup?.hide?.();
        this.cartUpdateUnsubscribe?.();
        this.cartUpdateUnsubscribe = null;
      });
    }

    isMobile() {
      return window.matchMedia(MQ_MOBILE).matches;
    }

    scrollScene(isMobile) {
      const scene = isMobile ? MOBILE_SCROLL_SCENE : DESKTOP_SCROLL_SCENE;
      const [act1, act2, act3] = this.scrollRatios;
      return {
        stops: scene.stops,
        spans: scene.spans.map((span, index) => span * (index === 0 ? act1 : index === 3 ? act3 : act2)),
      };
    }

    syncScrollLength() {
      const isMobile = this.isMobile();
      const viewportHeight = isMobile
        ? this.mSticky?.offsetHeight || window.innerHeight
        : window.innerHeight;
      const { spans } = this.scrollScene(isMobile);
      const travel = spans.reduce((total, span) => total + span, 0) * viewportHeight;
      this.style.setProperty('--dl-scrolly-scroll-height', `${travel}px`);
    }

    progress() {
      const isMobile = this.isMobile();
      const viewportHeight = isMobile
        ? this.mSticky?.offsetHeight || window.innerHeight
        : window.innerHeight;
      const track = isMobile ? this.mobileTrack : this.desktopTrack;
      if (!track || viewportHeight <= 0) return 0;
      const { stops, spans } = this.scrollScene(isMobile);
      const scrolled = Math.max(0, -track.getBoundingClientRect().top);
      let start = 0;
      for (let index = 0; index < spans.length; index++) {
        const end = start + spans[index] * viewportHeight;
        if (scrolled <= end) {
          return lerp(stops[index], stops[index + 1], clamp((scrolled - start) / (end - start), 0, 1));
        }
        start = end;
      }
      return 1;
    }

    updateScale() {
      this.syncScrollLength();
      if (this.isMobile()) {
        if (!this.mCanvas) return;
        const viewportWidth = this.mSticky?.clientWidth || window.innerWidth;
        const viewportHeight = this.mSticky?.clientHeight || window.innerHeight;
        const widthScale = viewportWidth / M_STAGE_W;
        const logicalHeight = viewportHeight / widthScale;
        const verticalScale = logicalHeight / M_STAGE_H;

        this.mCanvasScale = widthScale;
        this.style.setProperty('--dl-m-pad-top', `${40 * verticalScale}px`);
        this.style.setProperty('--dl-m-pad-bottom', `${20 * verticalScale}px`);
        this.style.setProperty('--dl-m-gap-40', `${40 * verticalScale}px`);
        this.style.setProperty('--dl-m-gap-12', `${12 * verticalScale}px`);
        this.style.setProperty('--dl-m-mask-height', `${80 * viewportHeight / M_STAGE_H}px`);
        this.style.setProperty('--dl-m-reel-pad-y', `${8 * viewportHeight / M_STAGE_H}px`);
        this.style.setProperty('--dl-m-hint-bottom', `${18 * viewportHeight / M_STAGE_H}px`);
        this.syncMobileReelHeight();
        this.mCanvas.style.height = `${logicalHeight}px`;
        this.mCanvas.style.transform = `scale(${widthScale})`;
        return;
      }
      this.mCanvasScale = 1;
      if (!this.stage) return;
      const scale = Math.min(window.innerWidth / STAGE_W, window.innerHeight / STAGE_H);
      this.stage.style.transform = `scale(${scale})`;
    }

    syncMobileReelHeight() {
      if (!this.mReels) return;
      const reelHeight = (items) => Array.from(items).reduce(
        (total, item) => total + item.scrollHeight,
        0,
      );
      const height = Math.max(
        reelHeight(this.mReel2Items),
        reelHeight(this.mReel3Items),
      );
      if (height > 0) {
        this.style.setProperty('--dl-m-reels-height', `${height}px`);
      }
    }

    updateReel(track, items, reel) {
      if (!track || !items.length) return;
      track.style.transform = `translate3d(0, ${REEL_FOCAL - REEL_STEP / 2 - reel * REEL_STEP}px, 0)`;
      items.forEach((item, index) => {
        const opacity = interp(reel, [index - 2, index - 1, index, index + 1, index + 2], [0, 0.4, 1, 0.4, 0]);
        const x = interp(reel, [index - 1, index, index + 1], [0, -REEL_SHIFT, 0]);
        item.style.opacity = String(opacity);
        item.style.transform = `translate3d(${x}px, 0, 0)`;
      });
    }

    updateMobileReel(items, reel) {
      if (!items.length) return;
      const travel = Math.max(40, (this.mReels?.clientHeight || 141) * 0.32);
      items.forEach((item, index) => {
        const delta = index - reel;
        const opacity = clamp(1 - Math.abs(delta), 0, 1);
        const y = clamp(delta, -1, 1) * travel;
        item.style.opacity = String(opacity);
        item.style.visibility = opacity < 0.01 ? 'hidden' : 'visible';
        item.style.transform = `translate3d(0, -50%, 0) translate3d(0, ${y}px, 0)`;
        item.style.fontWeight = '600';
      });
    }

    updateDesktop(p) {
      // 背景：0–0.84 米白，0.84–0.90 过渡到黑（进 Act3）
      const bgColor = interpColor(p, [0, 0.84, 0.9], ['#eeede8', '#eeede8', '#000000']);
      this.style.setProperty('--dl-scrolly-bg', bgColor);
      if (this.bg) this.bg.style.backgroundColor = bgColor;
      if (this.bottomFade) {
        const fadeColor = p >= 0.9 ? '#000' : bgColor;
        this.bottomFade.style.background = `linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, ${fadeColor} 100%)`;
        setOpacity(this.bottomFade, interp(p, [0, 0.28], [0, 1]));
      }
      this.style.setProperty('--dl-phone-edge', interpColor(p, [0.84, 0.9], ['#AAAFB4', '#D5CCBA']));

      // 手机：0.10–0.17 淡入；0.34–0.50 从 Act1 位飞到 Act2 居中并放大
      const phoneOpacity = interp(p, [0.1, 0.17], [0, 1]);
      const phoneRevealY = interp(p, [0.1, 0.17], [20, 0]);
      const phoneX = interp(p, [0.34, 0.5], [PHONE_S1_X, 0]);
      const phoneY = interp(p, [0.34, 0.5], [PHONE_S1_Y, 0]);
      const phoneScale = interp(p, [0.34, 0.5], [PHONE_S1_SCALE, PHONE_S2_SCALE]);
      if (this.phone) {
        this.phone.style.opacity = String(phoneOpacity);
        this.phone.style.transform = `translate3d(${phoneX}px, ${phoneY}px, 0) scale(${phoneScale})`;
      }
      if (this.phoneReveal) {
        this.phoneReveal.style.transform = `translate3d(0, ${phoneRevealY}px, 0)`;
      }

      // 手机屏幕：Act2 四张图、Act3 三张图依次切；Act3 最后两条文案共用 phone_6。
      const hasPhone5 = Boolean(this.querySelector('[data-scrolly-screen-key="5"]'));
      const hasPhone6 = Boolean(this.querySelector('[data-scrolly-screen-key="6"]'));
      const baseScreenOps = [
        interp(p, [0, 0.58, 0.61], [1, 1, 0]), // 屏1：开场就在，0.58–0.61 切走
        interp(p, [0.58, 0.61, 0.66, 0.69], [0, 1, 1, 0]), // 屏2
        interp(p, [0.66, 0.69, 0.74, 0.77], [0, 1, 1, 0]), // 屏3
        interp(p, [0.74, 0.77, 0.84, 0.87], [0, 1, 1, 0]), // 屏4
      ];
      const aiScreenOp = hasPhone5
        ? interp(p, [0.84, 0.87, 0.935, 0.95], [0, 1, 1, 0])
        : hasPhone6
          ? interp(p, [0.84, 0.87, 0.962, 0.975], [0, 1, 1, 0])
          : interp(p, [0.84, 0.87, 1], [0, 1, 1]);
      const phone5Op = hasPhone6
        ? interp(p, [0.935, 0.95, 0.962, 0.975], [0, 1, 1, 0])
        : interp(p, [0.935, 0.95, 1], [0, 1, 1]);
      const phone6Op = interp(p, [0.962, 0.975], [0, 1]);
      let baseScreenIndex = 0;
      this.screens.forEach((screen) => {
        const key = screen.dataset.scrollyScreenKey;
        const opacity = key === 'ai'
          ? aiScreenOp
          : key === '5'
            ? phone5Op
            : key === '6'
              ? phone6Op
              : (baseScreenOps[baseScreenIndex++] ?? 0);
        screen.style.opacity = String(opacity);
      });

      // 猫砂盆等 Act1 固定层：FADE_S–FADE_E 淡出
      const fixedOp = interp(p, [FADE_S, FADE_E], [1, 0]);
      this.act1Fixed.forEach((el) => setOpacity(el, fixedOp));

      // 标题和副标题首屏直接显示，只在 Act1 退出时淡出。
      if (this.heading) {
        setOpacity(this.heading, fixedOp);
        this.heading.style.transform = 'translate3d(0, 0, 0)';
        this.heading.style.visibility = fixedOp < 0.01 ? 'hidden' : 'visible';
      }
      if (this.sub) {
        setOpacity(this.sub, fixedOp);
        this.sub.style.transform = 'translate3d(0, 0, 0)';
        this.sub.style.visibility = fixedOp < 0.01 ? 'hidden' : 'visible';
      }

      // 其余 Act1 文案/卡片：括号里是「开始出现 → 出现完成」，之后一起淡出
      applyReveal(this.olive, p, 0.14, 0.2); // 左上 Olive 卡
      applyReveal(this.pee, p, 0.17, 0.23); // 右上通知卡
      applyReveal(this.callie, p, 0.2, 0.26); // 左下 Callie 卡

      setOpacity(this.sharedTitle, interp(p, [0.46, 0.54], [0, 1])); // 左上「What's included」
      if (this.sharedTitleText) {
        this.sharedTitleText.style.color = interpColor(p, [0.84, 0.9], ['#000000', '#ffffff']); // 跟背景一起变白
      }

      setOpacity(this.act2, interp(p, [0.46, 0.54, 0.84, 0.87], [0, 1, 1, 0])); // Act2 右侧列表
      const act3Opacity = interp(p, [0.87, 0.92], [0, 1]);
      setOpacity(this.act3, act3Opacity); // Act3 黑底列表
      setOpacity(this.act3LearnMore, act3Opacity); // 按钮独立位于底部遮罩上方

      this.updateReel(this.reel2Track, this.reel2Items, reelValue(p, [0.5, 0.84], this.reel2Items.length)); // Act2 逐条
      this.updateReel(this.reel3Track, this.reel3Items, reelValue(p, [0.92, 1], this.reel3Items.length)); // Act3 逐条

      if (this.hint) setOpacity(this.hint, interp(p, [0, 0.05], [1, 0])); // 下滑提示
    }

    updateAct1Phone(p) {
      const el = this.mPhoneAct1;
      if (!el) return;

      const morphS = 0.28; // 手机开始飞向 Act2 大图
      const morphE = 0.42; // 飞到位置
      const revealOp = interp(p, [0.12, 0.2], [0, 1]); // Act1 手机淡入
      const revealY = interp(p, [0.12, 0.2], [16, 0]);
      const fadeOut = interp(p, [0.42, 0.48], [1, 0]); // 飞完后把 Act1 那只手机藏掉，露出真正的大手机
      const opacity = Math.min(revealOp, fadeOut);

      el.style.opacity = String(opacity);
      el.style.visibility = opacity < 0.01 ? 'hidden' : 'visible';

      if (p < morphS) {
        el.style.transform = `translate3d(0, ${revealY}px, 0)`;
        if (this.mAct1) this.mAct1.style.zIndex = '';
        if (p >= 0.2) this.act1PhoneFrom = el.getBoundingClientRect();
        return;
      }

      if (this.mAct1) this.mAct1.style.zIndex = p < 0.48 ? '6' : '';
      if (!this.act1PhoneFrom) {
        el.style.transform = 'none';
        this.act1PhoneFrom = el.getBoundingClientRect();
      }

      const from = this.act1PhoneFrom;
      const to = this.mPhone ? this.mPhone.getBoundingClientRect() : from;
      const t = clamp((p - morphS) / (morphE - morphS || 1), 0, 1);
      const parentScale = this.mCanvasScale || 1;
      const dx = (to.left + to.width / 2 - (from.left + from.width / 2)) / parentScale;
      const dy = (to.top + to.height / 2 - (from.top + from.height / 2)) / parentScale;
      const scale = to.width / (from.width || 1);

      el.style.transform = `translate3d(${dx * t}px, ${dy * t}px, 0) scale(${lerp(1, scale, t)})`;
    }

    updateMobile(p) {
      // 背景：0–0.80 浅底，0.80–0.85 切黑
      const bgColor = interpColor(p, [0, 0.8, 0.85], ['#f3f1ec', '#f3f1ec', '#000000']);
      if (this.mBg) {
        this.mBg.style.backgroundColor = bgColor;
        // 渐变写在 background 简写上，只改 backgroundColor 盖不住；进 AI 时清掉图才能变黑
        this.mBg.style.backgroundImage = p >= 0.8 ? 'none' : '';
      }
      if (this.mPhoneFade) {
        this.mPhoneFade.style.backgroundImage = `linear-gradient(to bottom, rgba(0,0,0,0), ${bgColor})`;
      }
      this.style.setProperty('--dl-phone-edge', interpColor(p, [0.8, 0.85], ['#AAAFB4', '#D5CCBA']));

      // Act1 猫砂盆首屏直接显示，只保留退出淡出。
      const mFixedOp = interp(p, [0.28, 0.34], [1, 0]);
      setOpacity(this.mLitter, mFixedOp);
      setOpacity(this.mAct1BottomFade, interp(p, [0, 0.28], [0, 1]));
      if (this.mLitter) this.mLitter.style.transform = 'translate3d(-56%, 0, 0)';

      // 标题和副标题首屏直接显示，只在 Act1 退出时淡出。
      if (this.mHeading) {
        setOpacity(this.mHeading, mFixedOp);
        this.mHeading.style.transform = 'translate3d(0, 0, 0)';
        this.mHeading.style.visibility = mFixedOp < 0.01 ? 'hidden' : 'visible';
      }
      if (this.mSub) {
        setOpacity(this.mSub, mFixedOp);
        this.mSub.style.transform = 'translate3d(0, 0, 0)';
        this.mSub.style.visibility = mFixedOp < 0.01 ? 'hidden' : 'visible';
      }

      // 其他 Act1 元素：mReveal(开始出现, 出现完, 开始淡出, 淡出完)
      mReveal(this.mOlive, p, 0.1, 0.16, 0.28, 0.34); // Olive 卡
      mReveal(this.mPee, p, 0.13, 0.19, 0.28, 0.34); // 通知卡
      mReveal(this.mCallie, p, 0.16, 0.22, 0.28, 0.34); // Callie 卡
      this.updateAct1Phone(p); // 0.12 出现 → 0.28–0.42 飞到大手机 → 0.42–0.48 自己消失

      const act1Op = interp(p, [0.47, 0.48], [1, 0]); // 整层 Act1 卸掉
      setOpacity(this.mAct1, act1Op);

      const plansOp = interp(p, [0.32, 0.42], [0, 1]); // plans 层提前铺上
      setOpacity(this.mPlans, plansOp);

      if (this.mPlansTitle) {
        this.mPlansTitle.style.color = interpColor(p, [0.8, 0.85], ['#111111', '#ffffff']);
      }

      const showAiLabel = p >= 0.83;
      if (this.mPlan2) {
        this.mPlan2.style.display = showAiLabel ? 'none' : 'flex';
        setOpacity(this.mPlan2, showAiLabel ? 0 : interp(p, [0.42, 0.48], [0, 1]));
      }
      if (this.mPlan3) {
        this.mPlan3.style.display = showAiLabel ? 'flex' : 'none';
        setOpacity(this.mPlan3, showAiLabel ? interp(p, [0.83, 0.87], [0, 1]) : 0);
      }

      setOpacity(this.mPhone, interp(p, [0.42, 0.48], [0, 1])); // 大手机接棒

      const hasPhone5 = Boolean(this.querySelector('[data-m-screen-key="5"]'));
      const hasPhone6 = Boolean(this.querySelector('[data-m-screen-key="6"]'));
      const baseScreenOps = [
        interp(p, [0.42, 0.52, 0.55], [1, 1, 0]), // 屏1
        interp(p, [0.52, 0.55, 0.62, 0.65], [0, 1, 1, 0]), // 屏2
        interp(p, [0.62, 0.65, 0.72, 0.75], [0, 1, 1, 0]), // 屏3
        interp(p, [0.72, 0.75, 0.82, 0.86], [0, 1, 1, 0]), // 屏4
      ];
      const aiScreenOp = hasPhone5
        ? interp(p, [0.82, 0.86, 0.895, 0.915], [0, 1, 1, 0])
        : hasPhone6
          ? interp(p, [0.82, 0.86, 0.94, 0.96], [0, 1, 1, 0])
          : interp(p, [0.82, 0.86, 1], [0, 1, 1]);
      const phone5Op = hasPhone6
        ? interp(p, [0.895, 0.915, 0.94, 0.96], [0, 1, 1, 0])
        : interp(p, [0.895, 0.915, 1], [0, 1, 1]);
      const phone6Op = interp(p, [0.94, 0.96], [0, 1]);
      let baseScreenIndex = 0;
      this.mScreens.forEach((screen) => {
        const key = screen.dataset.mScreenKey;
        const opacity = key === 'ai'
          ? aiScreenOp
          : key === '5'
            ? phone5Op
            : key === '6'
              ? phone6Op
              : (baseScreenOps[baseScreenIndex++] ?? 0);
        screen.style.opacity = String(opacity);
      });

      const reelBg = interpColor(p, [0.8, 0.85], ['#f3f1ec', '#000000']);
      if (this.mBottomFade) {
        this.mBottomFade.style.background = `linear-gradient(to bottom, transparent 0%, ${reelBg} 100%)`;
      }
      if (this.mReels) this.mReels.style.backgroundColor = reelBg;
      setOpacity(this.mMask, interp(p, [0.32, 0.42], [0, 1])); // Act1 不出现底部遮罩
      setOpacity(this.mReel2, interp(p, [0.42, 0.48, 0.8, 0.83], [0, 1, 1, 0])); // Act2 reel
      setOpacity(this.mReel3, interp(p, [0.83, 0.87], [0, 1])); // Act3 reel
      this.updateMobileReel(this.mReel2Items, reelValue(p, [0.48, 0.8], this.mReel2Items.length));
      this.updateMobileReel(this.mReel3Items, reelValue(p, [0.87, 1], this.mReel3Items.length));

      if (this.mHint) {
        this.mHint.style.color = interpColor(p, [0.8, 0.85], ['rgba(0,0,0,0.4)', 'rgba(255,255,255,0.45)']);
        setOpacity(this.mHint, interp(p, [0, 0.06], [1, 0]));
      }
    }

    update() {
      const p = this.progress();
      if (this.isMobile()) this.updateMobile(p);
      else this.updateDesktop(p);
    }
  }

  if (!customElements.get('dl-page-scrolly-luma-y')) {
    customElements.define('dl-page-scrolly-luma-y', DlPageScrollyLumaY);
  }
})();
